import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import type { PlatformClient } from "../../platform/supabase";

type Status = {
  linked:boolean;
  claim?:{
    claimId:string;
    status:string;
    expiresAt:string;
    createdAt:string;
  } | null;
};

type CreatedClaim = {
  claimId:string;
  token:string;
  claimUrl:string;
  expiresAt:string;
};

function formatDate(value?:string|null){
  if(!value) return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"
  });
}

export function CenterWalletClaimPanel({
  client,
  schoolId,
  learnerId,
  canManage
}:{
  client:PlatformClient;
  schoolId:string;
  learnerId:string;
  canManage:boolean;
}){
  const [status,setStatus]=useState<Status|null>(null);
  const [created,setCreated]=useState<CreatedClaim|null>(null);
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");
  const [refresh,setRefresh]=useState(0);

  useEffect(()=>{
    let active=true;
    client.rpc("center_wallet_access_status",{
      p_school_id:schoolId,
      p_learner_id:learnerId
    }).then(({data,error})=>{
      if(!active) return;
      if(error) setError(error.message);
      else setStatus(data as Status);
    });
    return()=>{active=false;};
  },[client,schoolId,learnerId,refresh]);

  async function create(){
    setBusy(true);
    setError("");
    const {data,error}=await client.rpc("center_create_wallet_claim",{
      p_school_id:schoolId,
      p_learner_id:learnerId,
      p_valid_minutes:10080
    });
    setBusy(false);

    if(error){
      setError(error.message);
      return;
    }

    setCreated(data as CreatedClaim);
    setRefresh((value)=>value+1);
  }

  async function revoke(){
    if(!status?.claim?.claimId) return;
    setBusy(true);
    setError("");
    const {error}=await client.rpc("center_revoke_wallet_claim",{
      p_school_id:schoolId,
      p_claim_id:status.claim.claimId,
      p_reason:"Center에서 Wallet 연결 링크 취소"
    });
    setBusy(false);

    if(error){
      setError(error.message);
      return;
    }

    setCreated(null);
    setRefresh((value)=>value+1);
  }

  async function copy(value:string){
    await navigator.clipboard.writeText(value);
  }

  if(!status){
    return (
      <section className="wallet-connect-panel">
        <div className="panel-title"><strong>학생 Wallet</strong><span>연결 상태 확인 중…</span></div>
      </section>
    );
  }

  if(status.linked){
    return (
      <section className="wallet-connect-panel linked">
        <div>
          <p className="eyebrow">학생 Wallet</p>
          <strong>로그인 계정 연결 완료</strong>
          <p>학생이 자신의 Google 계정으로 진로성장 Wallet을 사용할 수 있습니다.</p>
        </div>
        <span className="status-pill good">연결됨</span>
      </section>
    );
  }

  return (
    <section className="wallet-connect-panel">
      <div className="wallet-connect-head">
        <div>
          <p className="eyebrow">학생 Wallet</p>
          <strong>학생 계정 연결</strong>
          <p>학교가 발급한 1회성 링크로만 기존 학생 기록과 로그인 계정을 연결합니다.</p>
        </div>
        {status.claim?.status==="OPEN" && <span className="status-pill">링크 활성</span>}
      </div>

      {error && <p className="error">{error}</p>}

      {created ? (
        <div className="wallet-connect-created">
          <QRCodeSVG value={created.claimUrl} size={152} level="M" />
          <div>
            <strong>7일 연결 링크</strong>
            <p className="wallet-link-text">{created.claimUrl}</p>
            <small>만료 {formatDate(created.expiresAt)}</small>
            <div className="wallet-button-row">
              <button className="secondary" onClick={()=>copy(created.claimUrl)}>링크 복사</button>
              <button className="secondary danger" onClick={revoke} disabled={busy}>링크 취소</button>
            </div>
          </div>
        </div>
      ) : status.claim?.status==="OPEN" ? (
        <div className="wallet-claim-existing">
          <div>
            <strong>활성 연결 링크가 있습니다.</strong>
            <p>보안을 위해 원문 토큰은 재표시하지 않습니다. 링크를 잃어버렸다면 취소 후 새로 발급하세요.</p>
            <small>만료 {formatDate(status.claim.expiresAt)}</small>
          </div>
          {canManage && (
            <button className="secondary danger" disabled={busy} onClick={revoke}>
              {busy ? "취소 중…" : "기존 링크 취소"}
            </button>
          )}
        </div>
      ) : (
        <div className="wallet-connect-empty">
          <p>아직 학생 로그인 계정이 연결되지 않았습니다.</p>
          {canManage ? (
            <button className="primary compact-button" disabled={busy} onClick={create}>
              {busy ? "발급 중…" : "Wallet 연결 링크 발급"}
            </button>
          ) : (
            <small>학교 관리자에게 Wallet 연결 링크 발급을 요청하세요.</small>
          )}
        </div>
      )}
    </section>
  );
}
